import { openWorldMobDangerOfMap } from '@kenocyber/shared';
import Phaser from 'phaser';
import type { OpenWorldCreatureCards } from '../open-world/connection';
import { openWorldMapBodyAnimKey, openWorldMapBodyTextureKey } from './open-world-map-art';

/**
 * **Os mobs, desenhados: um corpo que persegue, uma barra que cai, uma morte
 * que não se confunde com nada.**
 *
 * ## Três camadas, e a ordem entre elas é o contrato
 *
 * 1. **O corpo.** O sprite animado, virado para onde a autoridade disse que ele
 *    aponta. É a única camada que existe sempre.
 * 2. **O estado.** A barra de vida, e as tintas de veneno e sangramento. Vive
 *    por cima do corpo e diz o que o corpo não consegue dizer sozinho.
 * 3. **Os efeitos.** Impactos, ataques, nascimentos e mortes: coisas que
 *    acontecem uma vez, saem de eventos, e são esquecidas.
 *
 * O LOD corta de baixo para cima e nunca chega às duas primeiras: **um mob, a
 * barra dele, um ataque e um impacto confirmado desenham-se sempre**, por mais
 * cheia que a vista esteja. O que desaparece primeiro é a poeira, e depois os
 * estilhaços — decoração, e só decoração.
 *
 * ## Um sprite só, e está declarado
 *
 * **Cada alienígena usa a arte do MAPA dele** (gate V28I.BI). `alienN` para
 * `mapIndex = N`, resolvido por `mapCatalogEntry` — a mesma fonte canónica que
 * decide a espécie, a vida e a escala. Não há aqui segunda tabela de identidade
 * visual, e não há recuo para o índice 1: um mapa sem arte é recusado com o
 * índice no texto, e a cena reporta a recusa.
 *
 * A folha é pedida **sob procura**, quando o primeiro alienígena daquele mapa
 * chega à vista: o `preload` corre antes de o servidor dizer o que há no
 * quadrante, exatamente como acontece com os cascos. Até a folha chegar, o corpo
 * desenha-se com um marcador de lugar — nunca com a arte de outro mapa.
 *
 * A **tinta da banda de perigo** continua a existir e não mudou: ela diz o que
 * está a acontecer ao corpo, e a arte diz que corpo é.
 *
 * ## O que ele nunca decide
 *
 * Vida, dano, alvo, morte ou renascimento. Cada barra vem da vida autoritativa;
 * cada impacto vem de um evento que a sala já produziu; e uma morte só é
 * desenhada quando o evento `death` chega.
 *
 * **Um renascimento não explode.** Ele brilha e cresce — porque uma explosão no
 * nascimento seria a mesma imagem da morte, e um jogador que a visse ao longe
 * julgaria ter matado uma coisa que acabou de aparecer.
 *
 * ## Pendente de avaliação
 *
 * A aparência **não está aprovada**, e nenhuma alegação de desempenho é feita
 * aqui: nada foi medido num navegador.
 */

const TEXTURE = {
  spark: 'open-world-mob-spark',
  ring: 'open-world-mob-ring',
  /**
   * **O marcador de lugar do corpo** (gate V28I.BI).
   *
   * Um disco baço, e não a arte de outro mapa: enquanto a folha do mapa não
   * chega, o que se vê tem de ser legivelmente "ainda não carregou" em vez de
   * uma criatura errada. Vive um instante, e o corpo real substitui-o no quadro
   * em que a folha existe.
   */
  body: 'open-world-mob-body-pending',
} as const;

const BODY_PX = 64;

const SPARK_PX = 12;
const RING_PX = 64;

/**
 * Os tetos das piscinas. O primeiro a ser cortado é o mais decorativo.
 *
 * Números provisórios: escolhidos para um quadrante com doze mobs a serem
 * atacados ao mesmo tempo, e não medidos.
 */
const SPARK_MAX = 96;
const RING_MAX = 24;

const SPARK_LIFE_MS = 260;
const RING_LIFE_MS = 340;
const DEATH_LIFE_MS = 520;

/**
 * A tinta por banda de perigo, 1..5.
 *
 * Enquanto houver um corpo só, é isto que distingue um alienígena de nível um
 * de um de nível cinco à distância — e cai sozinho no dia em que cada mapa
 * tiver a arte dele.
 */
const DANGER_TINT = [0x9fe8b0, 0x8fd6ff, 0xd2a6ff, 0xffb066, 0xff6b7a] as const;

const POISON_TINT = 0x7dff9b;
const BLEED_TINT = 0xff5a5a;
const HIT_TINT = 0xfff2c4;
const ATTACK_TINT = 0xff8a3d;
const SPAWN_TINT = 0x9fe8ff;

/** Um mob, tal como a cena precisa de o desenhar. */
export interface MobDrawState {
  readonly id: string;
  readonly generation: number;
  readonly mapIndex: number;
  readonly x: number;
  readonly y: number;
  readonly dirX: number;
  readonly dirY: number;
  readonly radiusMu: number;
  readonly healthMilli: string;
  readonly maxHealthMilli: string;
  readonly stance: 'idle' | 'chasing' | 'returning';
  readonly poisoned: boolean;
  readonly bleeding: boolean;
  /**
   * **As penalidades que uma carta lhe está a fazer.**
   *
   * Elas não são desenhadas por esta família: os sinais de debuff têm a sua
   * própria, aprovada no duelo, e a cena aponta-a a este corpo pela posição e
   * pelo raio que este descritor já traz. O campo viaja aqui porque é aqui que a
   * cena tem o alienígena inteiro à mão.
   */
  readonly cards: OpenWorldCreatureCards;
}

/** O que lhe aconteceu nesta trama. Desenhado uma vez, e esquecido. */
export interface MobDrawEvent {
  readonly id: string;
  readonly ordinal: number;
  readonly kind: 'hit' | 'attack' | 'death' | 'spawn';
  readonly x: number;
  readonly y: number;
}

interface Mote {
  readonly image: Phaser.GameObjects.Image;
  bornMs: number;
  lifeMs: number;
  vx: number;
  vy: number;
  fromScale: number;
  toScale: number;
}

interface MobView {
  readonly body: Phaser.GameObjects.Sprite;
  readonly bar: Phaser.GameObjects.Graphics;
  /**
   * A chave com que o corpo está desenhado AGORA.
   *
   * Ela existe para a troca acontecer **uma vez**: enquanto a folha do mapa não
   * chega, a vista fica no marcador de lugar; no quadro em que a folha existe, o
   * corpo passa para ela e a animação começa. Um `mapIndex` nunca muda para a
   * mesma entidade, portanto isto não é um estado que oscile.
   */
  bodyKey: string;
  /** Onde ele está a ser DESENHADO. Interpolado, nunca a posição crua. */
  drawX: number;
  drawY: number;
  /** Até quando o corpo está a piscar de um golpe. Zero quando não está. */
  flashUntilMs: number;
}

interface SparkGraphics {
  fillStyle(color: number, alpha?: number): SparkGraphics;
  fillCircle(x: number, y: number, radius: number): SparkGraphics;
  lineStyle(width: number, color: number, alpha?: number): SparkGraphics;
  strokeCircle(x: number, y: number, radius: number): SparkGraphics;
  generateTexture(key: string, width?: number, height?: number): unknown;
  destroy(): void;
}

interface TextureScene {
  readonly textures: { exists(key: string): boolean };
  readonly make: {
    graphics(config: { x: number; y: number }, addToScene: boolean): SparkGraphics;
  };
}

/** O disco que segura o lugar do corpo até a folha do mapa chegar. */
function makeBodyPlaceholderTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.body)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.fillStyle(0x2a3a4a, 1);
  graphics.fillCircle(BODY_PX / 2, BODY_PX / 2, BODY_PX / 2 - 2);
  graphics.generateTexture(TEXTURE.body, BODY_PX, BODY_PX);
  graphics.destroy();
}

/** Um ponto branco, para ser tingido. Estilhaços, sangue e veneno saem daqui. */
function makeSparkTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.spark)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.fillStyle(0xffffff, 1);
  graphics.fillCircle(SPARK_PX / 2, SPARK_PX / 2, SPARK_PX / 2 - 1);
  graphics.generateTexture(TEXTURE.spark, SPARK_PX, SPARK_PX);
  graphics.destroy();
}

/** Um anel fino. O ataque abre-o para fora; o nascimento fecha-o para dentro. */
function makeRingTexture(scene: TextureScene): void {
  if (scene.textures.exists(TEXTURE.ring)) {
    return;
  }
  const graphics = scene.make.graphics({ x: 0, y: 0 }, false);
  graphics.lineStyle(3, 0xffffff, 1);
  graphics.strokeCircle(RING_PX / 2, RING_PX / 2, RING_PX / 2 - 3);
  graphics.generateTexture(TEXTURE.ring, RING_PX, RING_PX);
  graphics.destroy();
}

export class OpenWorldMobArt {
  private readonly views = new Map<string, MobView>();
  private readonly sparks: Mote[] = [];
  private readonly rings: Mote[] = [];
  /** O último ordinal desenhado por mob. Um evento repetido não repete o efeito. */
  private readonly seenEvent = new Map<string, number>();

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly depth: number,
  ) {
    const target = scene as unknown as TextureScene;
    makeSparkTexture(target);
    makeRingTexture(target);
    makeBodyPlaceholderTexture(target);
  }

  update(
    mobs: readonly MobDrawState[],
    events: readonly MobDrawEvent[],
    nowMs: number,
    worldUnits: (mu: number) => number,
  ): void {
    const alive = new Set<string>();

    for (const mob of mobs) {
      const key = mob.id;
      alive.add(key);
      const targetX = worldUnits(mob.x);
      const targetY = worldUnits(mob.y);
      const view = this.viewFor(key, targetX, targetY);
      /*
       * A mesma interpolação dos cascos: a autoridade fala a 20 Hz e o ecrã
       * desenha a 60, e o que fecha a diferença é aproximar do último facto —
       * nunca extrapolar para além dele, que seria inventar movimento.
       */
      view.drawX += (targetX - view.drawX) * 0.35;
      view.drawY += (targetY - view.drawY) * 0.35;

      const radius = worldUnits(mob.radiusMu);
      const body = view.body;
      /*
       * **A arte do mapa dele, e nunca a de outro** (gate V28I.BI).
       *
       * A chave sai do `mapIndex` autoritativo. Enquanto a folha não existir, o
       * corpo fica no marcador de lugar; assim que ela existir, ele troca e a
       * animação daquele mapa começa. Uma reconstrução — travessia, reconexão,
       * reentrada no quadrante — destrói as vistas e refaz este mesmo caminho,
       * com a folha já em cache.
       */
      const bodyKey = openWorldMapBodyTextureKey('mob', mob.mapIndex);
      if (view.bodyKey !== bodyKey && this.scene.textures.exists(bodyKey)) {
        body.setTexture(bodyKey);
        view.bodyKey = bodyKey;
        const animKey = openWorldMapBodyAnimKey('mob', mob.mapIndex);
        if (this.scene.anims.exists(animKey)) {
          body.play(animKey);
        }
      }
      body.setPosition(view.drawX, view.drawY);
      body.setDisplaySize(radius * 2.4, radius * 2.4);
      /*
       * O nariz aponta para onde a autoridade disse. `dirX/dirY` é uma direção
       * normalizada em inteiros, e `atan2` de dois inteiros é estável o
       * suficiente para uma rotação — não entra em nenhuma conta autoritativa.
       */
      body.setRotation(Math.atan2(mob.dirY, mob.dirX) + Math.PI / 2);

      /*
       * **A tinta diz o estado, e a prioridade entre elas é fixa.**
       *
       * Um golpe recente ganha a tudo — é o feedback mais imediato que existe.
       * Depois o sangramento, depois o veneno, e por fim a banda de perigo, que
       * é o que ele é quando nada lhe está a acontecer.
       */
      if (nowMs < view.flashUntilMs) {
        body.setTint(HIT_TINT);
      } else if (mob.bleeding) {
        body.setTint(BLEED_TINT);
      } else if (mob.poisoned) {
        body.setTint(POISON_TINT);
      } else {
        body.setTint(DANGER_TINT[openWorldMobDangerOfMap(mob.mapIndex) - 1] ?? DANGER_TINT[0]);
      }
      /* Quem persegue vem inteiro; quem volta para casa esmorece um pouco. */
      body.setAlpha(mob.stance === 'returning' ? 0.75 : 1);

      this.drawBar(view, mob, radius);
    }

    for (const [key, view] of this.views) {
      if (alive.has(key)) {
        continue;
      }
      view.body.destroy();
      view.bar.destroy();
      this.views.delete(key);
      this.seenEvent.delete(key);
    }

    for (const event of events) {
      const seen = this.seenEvent.get(event.id) ?? 0;
      if (event.ordinal <= seen) {
        continue;
      }
      this.seenEvent.set(event.id, event.ordinal);
      this.playEvent(event, nowMs, worldUnits);
    }

    this.stepMotes(this.sparks, nowMs);
    this.stepMotes(this.rings, nowMs);
  }

  /**
   * A barra de vida: vermelha, como a dos cascos e a da caça.
   *
   * Desenhada por mob e destruída com ele. **Nunca cortada pelo LOD**: uma
   * barra que desaparecesse numa vista cheia esconderia exatamente o facto de
   * que alguém está quase a morrer.
   */
  private drawBar(view: MobView, mob: MobDrawState, radius: number): void {
    const max = BigInt(mob.maxHealthMilli);
    const current = BigInt(mob.healthMilli);
    const fraction = max <= 0n ? 0 : Number((current * 1000n) / max) / 1000;
    const width = radius * 2.2;
    view.bar.clear();
    view.bar.setPosition(view.drawX, view.drawY);
    view.bar.fillStyle(0x1a0a0f, 0.75);
    view.bar.fillRect(-width / 2, -radius - 8, width, 3);
    view.bar.fillStyle(0xff4d5e, 1);
    view.bar.fillRect(-width / 2, -radius - 8, width * Math.max(0, Math.min(1, fraction)), 3);
  }

  private playEvent(event: MobDrawEvent, nowMs: number, worldUnits: (mu: number) => number): void {
    const x = worldUnits(event.x);
    const y = worldUnits(event.y);
    const view = this.views.get(event.id);
    switch (event.kind) {
      case 'hit': {
        /* O corpo pisca, e três faíscas saem. A piscadela nunca é cortada. */
        if (view !== undefined) {
          view.flashUntilMs = nowMs + 90;
        }
        for (let index = 0; index < 3; index += 1) {
          this.spark(x, y, nowMs, HIT_TINT, SPARK_LIFE_MS);
        }
        break;
      }
      case 'attack': {
        /* Um anel que abre. É o aviso de que ele acertou em alguém. */
        this.ring(x, y, nowMs, ATTACK_TINT, 0.4, 1.4, RING_LIFE_MS);
        break;
      }
      case 'death': {
        /*
         * **A morte é inconfundível**: um anel grande e um punhado de
         * estilhaços que se abrem devagar. Nada disto se parece com o
         * nascimento, que fecha em vez de abrir.
         */
        this.ring(x, y, nowMs, BLEED_TINT, 0.6, 2.6, DEATH_LIFE_MS);
        for (let index = 0; index < 10; index += 1) {
          this.spark(x, y, nowMs, BLEED_TINT, DEATH_LIFE_MS);
        }
        break;
      }
      case 'spawn': {
        /* Um anel que FECHA. Sem estilhaços: nascer não parte nada. */
        this.ring(x, y, nowMs, SPAWN_TINT, 2.2, 0.5, RING_LIFE_MS);
        break;
      }
      default: {
        /* Um tipo que este código não conhece não desenha nada. */
        break;
      }
    }
  }

  private spark(x: number, y: number, nowMs: number, tint: number, lifeMs: number): void {
    /*
     * **O corte do LOD acontece aqui, e só aqui.** Um estilhaço a mais é
     * simplesmente não criado; nada do que a autoridade disse é escondido.
     */
    if (this.sparks.length >= SPARK_MAX) {
      return;
    }
    const angle = Math.random() * Math.PI * 2;
    const speed = 0.06 + Math.random() * 0.09;
    const image = this.scene.add
      .image(x, y, TEXTURE.spark)
      .setDepth(this.depth + 2)
      .setTint(tint)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.sparks.push({
      image,
      bornMs: nowMs,
      lifeMs,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      fromScale: 1,
      toScale: 0.2,
    });
  }

  private ring(
    x: number,
    y: number,
    nowMs: number,
    tint: number,
    fromScale: number,
    toScale: number,
    lifeMs: number,
  ): void {
    if (this.rings.length >= RING_MAX) {
      return;
    }
    const image = this.scene.add
      .image(x, y, TEXTURE.ring)
      .setDepth(this.depth + 2)
      .setTint(tint)
      .setBlendMode(Phaser.BlendModes.ADD);
    this.rings.push({ image, bornMs: nowMs, lifeMs, vx: 0, vy: 0, fromScale, toScale });
  }

  private stepMotes(motes: Mote[], nowMs: number): void {
    for (let index = motes.length - 1; index >= 0; index -= 1) {
      const mote = motes[index];
      if (mote === undefined) {
        continue;
      }
      const age = nowMs - mote.bornMs;
      if (age >= mote.lifeMs) {
        mote.image.destroy();
        motes.splice(index, 1);
        continue;
      }
      const t = age / mote.lifeMs;
      mote.image.setPosition(mote.image.x + mote.vx * 16, mote.image.y + mote.vy * 16);
      mote.image.setScale(mote.fromScale + (mote.toScale - mote.fromScale) * t);
      mote.image.setAlpha(1 - t);
    }
  }

  /**
   * A vista de um mob, criada onde ele ESTA.
   *
   * O primeiro quadro coloca; os seguintes seguem. Nascer na origem do
   * quadrante e deslizar dali ate ao sitio certo foi exatamente o defeito que a
   * camara teve na primeira inspecao, e a resposta e a mesma.
   */
  private viewFor(key: string, atX: number, atY: number): MobView {
    const existing = this.views.get(key);
    if (existing !== undefined) {
      return existing;
    }
    /*
     * Nasce no marcador de lugar, SEMPRE. A arte do mapa entra no primeiro
     * quadro em que a folha existir — e quem cria a vista não sabe se ela já
     * chegou.
     */
    const body = this.scene.add.sprite(0, 0, TEXTURE.body).setDepth(this.depth);
    const bar = this.scene.add.graphics().setDepth(this.depth + 1);
    const view: MobView = {
      body,
      bar,
      bodyKey: TEXTURE.body,
      drawX: atX,
      drawY: atY,
      flashUntilMs: 0,
    };
    this.views.set(key, view);
    return view;
  }

  destroy(): void {
    for (const view of this.views.values()) {
      view.body.destroy();
      view.bar.destroy();
    }
    this.views.clear();
    this.seenEvent.clear();
    for (const mote of [...this.sparks, ...this.rings]) {
      mote.image.destroy();
    }
    this.sparks.length = 0;
    this.rings.length = 0;
  }
}
